import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, User } from 'lucide-react';
import { useTheme } from '../../ThemeContext.tsx';
import { apiService } from '../../services/api.ts';

type AvatarSectionProps = {
    onLogout: () => void;
    compact?: boolean;
};

const AvatarSection: React.FC<AvatarSectionProps> = ({ onLogout, compact = false }) => {
    const { colors } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [userInfo, setUserInfo] = useState<{ name?: string; email?: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);

    // Get user info on mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            // Check sessionStorage first
            const cached = sessionStorage.getItem('grippy_user_info');
            if (cached) {
                try {
                    const userData = JSON.parse(cached);
                    setUserInfo(userData);
                    setIsLoading(false);
                    return;
                } catch (error) {
                    console.error('Failed to parse cached user info:', error);
                    sessionStorage.removeItem('grippy_user_info');
                }
            }

            // Fetch from API if not cached
            try {
                const response = await apiService.getCurrentUser();
                if (response.success && response.user_info) {
                    const userData = {
                        name: response.user_info.name || response.user_info.userPrincipalName || response.user_info.email?.split('@')[0],
                        email: response.user_info.email || response.user_info.userPrincipalName
                    };
                    setUserInfo(userData);
                    sessionStorage.setItem('grippy_user_info', JSON.stringify(userData));
                } else {
                    setUserInfo({
                        name: 'User',
                        email: 'user@example.com'
                    });
                }
            } catch (error) {
                console.error('Failed to fetch user info:', error);
                setUserInfo({
                    name: 'User',
                    email: 'user@example.com'
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserInfo();
    }, []);

    // Update button position when dropdown opens
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            // console.log('Button position:', rect);
            setButtonRect(rect);
        }
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handle = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('#user-avatar-dropdown') || target.closest('#user-avatar-button')) return;
            setIsOpen(false);
        };
        window.addEventListener('click', handle);
        return () => window.removeEventListener('click', handle);
    }, [isOpen]);

    const handleLogout = () => {
        sessionStorage.removeItem('grippy_user_info');
        onLogout();
        setIsOpen(false);
    };

    const dropdownContent = isOpen && buttonRect && (() => {
        // console.log('Creating dropdown content, buttonRect:', buttonRect);
        return (
            <div
                id="user-avatar-dropdown"
                className={`fixed z-[9999] ${colors.bg.primary} border ${colors.border.primary} rounded-lg shadow-xl overflow-hidden`}
                style={{
                    top: `${buttonRect.top - 60}px`, // Position it closer to the button
                    left: `${buttonRect.left - 5}px`,
                    width: '240px'
                }}
                onClick={(e) => {
                    console.log('Dropdown clicked');
                    e.stopPropagation();
                }}
            >
                {/* Menu Options */}
                <div className="py-1">
                    {/* Sign Out Button */}
                    <button
                        onClick={handleLogout}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-sm ${colors.text.primary} hover:bg-red-50 hover:text-red-600 transition-colors`}
                    >
                        <LogOut size={16} className="text-red-500" />
                        <span>Sign Out</span>
                    </button>
                </div>
            </div>
        );
    })();

    return (
        <>

            {/* User Button */}
            <button
                ref={buttonRef}
                id="user-avatar-button"
                onClick={() => {
                    setIsOpen(!isOpen);
                }}
                className={compact
                    ? `flex items-center justify-center w-10 h-10 rounded-full ${colors.bg.tertiary} ${colors.text.primary} hover:${colors.bg.secondary} transition-colors`
                    : `flex items-center gap-2 p-2 rounded-lg ${colors.bg.tertiary} ${colors.text.primary} hover:${colors.bg.secondary} transition-colors w-full`}
                title={userInfo ? `${userInfo.name || 'User'}${userInfo.email ? `\n${userInfo.email}` : ''}` : 'User Menu'}
            >
                <div className={compact ? "w-7 h-7 rounded-full bg-[#13670B] flex items-center justify-center" : "w-6 h-6 rounded-full bg-[#13670B] flex items-center justify-center flex-shrink-0"}>
                    <User size={compact ? 16 : 14} className="text-white" />
                </div>
                {!compact && (
                    <div className="flex flex-col items-start min-w-0 flex-1">
                        {isLoading ? (
                            <div className={`text-xs ${colors.text.tertiary}`}>Loading...</div>
                        ) : (
                            <>
                                <div className={`text-xs font-medium ${colors.text.primary} truncate flex items-center gap-2 max-w-[180px]`}>
                                    {(userInfo?.name || 'User').length > 30
                                        ? `${(userInfo?.name || 'User').substring(0, 30)}...`
                                        : (userInfo?.name || 'User')}
                                </div>
                                {userInfo?.email && (
                                    <div className={`text-xs ${colors.text.tertiary} truncate flex items-center gap-2 max-w-[180px]`}>
                                        {userInfo.email.length > 30
                                            ? `${userInfo.email.substring(0, 30)}...`
                                            : userInfo.email}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </button>

            {/* Portal the dropdown to document.body */}
            {typeof window !== 'undefined' && createPortal(dropdownContent, document.body)}
        </>
    );
};

export default AvatarSection;

